import { HoldingsResponse, MarketQuotesResponse } from '../types/upstox';

export type CandleInterval = '1minute' | '30minute' | 'day' | 'week' | 'month';

interface CandleData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  oi: number;
}

interface CandleResponse {
  status: string;
  data: {
    candles: [string, number, number, number, number, number, number][];
  };
}

export class UpstoxDataService {
  private readonly baseUrl = 'https://api.upstox.com/v2';
  
  constructor(private token: string) {}

  async getHoldings(): Promise<HoldingsResponse> {
    const response = await fetch(`${this.baseUrl}/portfolio/long-term-holdings`, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Accept': 'application/json'
      }
    });

    if (response.status === 500) {
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch holdings');
    }

    const data = await response.json();
    console.log('Holdings data fetched:', data); // Added log
    return data;
  }

  async getCandleData(
    instrumentKey: string,
    interval: CandleInterval,
    toDate: string,
    fromDate?: string
  ): Promise<CandleData[]> {
    // Format dates to YYYY-MM-DD
    const formattedToDate = new Date(toDate).toISOString().split('T')[0];
    const formattedFromDate = fromDate 
      ? new Date(fromDate).toISOString().split('T')[0]
      : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days ago by default

    // Construct URL with path parameters instead of query parameters
    const url = `${this.baseUrl}/historical-candle/${instrumentKey}/${interval}/${formattedToDate}/${formattedFromDate}`;

    console.log('Fetching candle data:', url); // Debug log

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Candle data error:', errorData); // Debug log
      throw new Error(errorData.errors?.[0]?.message || 'Failed to fetch candle data');
    }

    const data: CandleResponse = await response.json();
    
    return data.data.candles.map(([timestamp, open, high, low, close, volume, oi]) => ({
      timestamp,
      open,
      high,
      low,
      close,
      volume,
      oi
    }));
  }

  async getMarketQuotes(instrumentKeys: string[]): Promise<MarketQuotesResponse> {
    const params = new URLSearchParams({
      instrument_key: instrumentKeys.join(',')
    });

    const response = await fetch(`${this.baseUrl}/market-quote/quotes?${params}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch market quotes');
    }

    return response.json();
  }
}
